/*!

=========================================================
* Black Dashboard PRO React - v1.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/black-dashboard-pro-react
* Copyright 2020 Creative Tim (https://www.creative-tim.com)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
import { useMemo } from "react";
import apiService from "services/api";
import { useQuery } from "react-query";
import ReactRoundedImage from "react-rounded-image";
import { getPrevious12MonthsWeekRange } from "utils/dateHelper";
import LineGraph from "../../components/LineChart";

// reactstrap components
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  CardText,
  FormGroup,
  Form,
  Input,
  Row,
  Col,
} from "reactstrap";
import { capitalize } from "utils/capitalize";

const initialData = {
  active: false,
  annualLeaveDaysCount: 0,
  birthday: "",
  compensationLeaveHoursCount: 0,
  email: "",
  employeeType: "",
  fullName: "",
  officialStartDate: "",
  region: "",
  sickLeaveDaysCount: "",
  status: "",
  unpaidLeaveDaysCount: "",
  username: "",
};

const previous12MonthsWeekRange = getPrevious12MonthsWeekRange();
const monthMap = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

export const Profile = ({ staff }) => {
  var weeklyThreshold, monthlyThreshold, yearlyThreshold;

  switch (staff?.role?.name) {
    case "teacher":
      weeklyThreshold = 12;
      break;
    case "head teacher" || "administrative staff + teacher":
      weeklyThreshold = 10;
      break;
    case "superadmin" || "part-time teacher":
      weeklyThreshold = 8;
      break;
    default:
      weeklyThreshold = 0;
  }

  monthlyThreshold = 4 * weeklyThreshold;
  yearlyThreshold = 12 * monthlyThreshold;

  // Charts
  const lessonRecordDataset = useMemo(() => {
    if (!staff) return new Map();
    const weekMap = new Map();
    for (const [month, weeks] of previous12MonthsWeekRange.entries()) {
      weekMap.set(month, Array(weeks.length).fill(0));
    }
    staff?.lessonRecords?.forEach((lessonRecord) => {
      if (
        lessonRecord.isCancelled ||
        !lessonRecord.students.every((student) => student.studentReportAdded)
      )
        return;
      const lessonRecordDate = new Date(lessonRecord.date);
      const lessonRecordMonth = lessonRecordDate.getMonth();
      const weekIndex = previous12MonthsWeekRange
        ?.get(lessonRecordMonth)
        ?.findIndex(({ start, end }) => {
          return start <= lessonRecordDate && lessonRecordDate < end;
        });
      if (weekMap.get(lessonRecordMonth) === undefined) return;

      weekMap.get(lessonRecordMonth)[weekIndex] =
        weekMap.get(lessonRecordMonth)[weekIndex] + lessonRecord.lessonDuration;
    });
    return weekMap;
  }, [staff]);

  const lessonRecordsDatasetValues = useMemo(
    () => [...lessonRecordDataset.values()],
    [lessonRecordDataset],
  );
  const lessonRecordsDatasetKeys = useMemo(
    () => [...lessonRecordDataset.keys()],
    [lessonRecordDataset],
  );

  const monthlyDataset = {
    datasetIdKey: "id",
    labels: lessonRecordsDatasetKeys.map((monthIndex) => monthMap[monthIndex]),
    datasets: [
      {
        id: 1,
        label: "Teaching Hours",
        data: lessonRecordsDatasetValues.map((weeklyValues) =>
          weeklyValues.reduce((acc, curr) => acc + curr, 0),
        ),
      },
    ],
  };

  const weeklyDataset = {
    datasetIdKey: "id",
    labels: [...previous12MonthsWeekRange.values()]
      .map((weekInMonth, monthIndex) =>
        weekInMonth.map(
          (_, weekIndex) => `Week ${weekIndex + 1}, ${monthMap[monthIndex]}`,
        ),
      )
      .slice(-5)
      .flat(),
    datasets: [
      {
        id: 1,
        label: "Teaching Hours",
        data: lessonRecordsDatasetValues.slice(-5).flat(),
      },
    ],
  };

  const hoursStats = useMemo(() => {
    if (!lessonRecordsDatasetValues.length)
      return { weeklyHours: 0, monthlyHours: 0, yearlyHours: 0 };

    return {
      weeklyHours: lessonRecordsDatasetValues?.at(-1),
      monthlyHours: lessonRecordsDatasetValues
        ?.at(-1)
        .reduce((acc, curr) => acc + curr, 0),
      yearlyHours: lessonRecordsDatasetValues?.reduce(
        (monthlyAcc, monthlyCurr) =>
          monthlyAcc +
          monthlyCurr.reduce(
            (weeklyAcc, weeklyCurr) => weeklyAcc + weeklyCurr,
            0,
          ),
        0,
      ),
    };
  }, [lessonRecordsDatasetValues]);

  return (
    <>
      <div className="content">
        <Row>
          <Col md="4" className="order-1 order-md-1">
            <Card className="card-user">
              <CardBody>
                <CardText />
                <div className="author">
                  <div className="block block-one" />
                  <div className="block block-two" />
                  <div className="block block-three" />
                  <div className="block block-four" />

                  <div className="d-flex justify-content-center mb-2">
                    <ReactRoundedImage
                      image={"./genius-logo512.png"}
                      roundedSize="1"
                      imageWidth="150"
                      imageHeight="150"
                    />
                  </div>
                  <h4 className="title">{staff?.fullName || ""}</h4>
                  <p className="description">
                    {capitalize(staff?.role?.name) || ""}
                  </p>
                </div>
              </CardBody>
            </Card>
          </Col>
          <Col md="8" className="order-2 order-md-2">
            <Card>
              <CardHeader>
                <h4 className="title">Teaching</h4>
              </CardHeader>
              <CardBody>
                <Form>
                  <Row>
                    <Col className="pr-md-1" md="4">
                      <FormGroup>
                        <label className="text-white">
                          <b>Weekly</b> Hours
                        </label>
                        <Input
                          disabled
                          value={
                            typeof hoursStats.weeklyHours === "number"
                              ? hoursStats.weeklyHours?.toFixed(1)
                              : hoursStats.weeklyHours +
                                  "/" +
                                  weeklyThreshold || "Error"
                          }
                          className={
                            hoursStats.weeklyHours > weeklyThreshold
                              ? "text-success"
                              : "text-danger"
                          }
                          type="text"
                        />
                      </FormGroup>
                    </Col>
                    <Col className="pr-md-1" md="4">
                      <FormGroup>
                        <label className="text-white">
                          <b>Monthly</b> Hours
                        </label>
                        <Input
                          disabled
                          value={
                            hoursStats.monthlyHours.toFixed(1) +
                              "/" +
                              monthlyThreshold || "Error"
                          }
                          className={
                            hoursStats.monthlyHours > monthlyThreshold
                              ? "text-success"
                              : "text-danger"
                          }
                          type="text"
                        />
                      </FormGroup>
                    </Col>
                    <Col className="pr-md-1" md="4">
                      <FormGroup>
                        <label className="text-white">
                          <b>Yearly</b> Hours
                        </label>
                        <Input
                          disabled
                          value={
                            hoursStats.yearlyHours.toFixed(1) +
                              "/" +
                              yearlyThreshold || "Error"
                          }
                          className={
                            hoursStats.yearlyHours > yearlyThreshold
                              ? "text-success"
                              : "text-danger"
                          }
                          type="text"
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col className="pr-md-1" md="6">
                      <FormGroup>
                        <label>Total Teaching Hours</label>
                        <Input
                          disabled
                          value={staff?.totalTeachingHours || "Error"}
                          className="text-white"
                          type="text"
                        />
                      </FormGroup>
                    </Col>
                    <Col className="pl-md-1" md="6">
                      <FormGroup>
                        <label>Total Training Hours</label>
                        <Input
                          disabled
                          value={staff?.totalTrainingHours || "0"}
                          className="text-white"
                          type="text"
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                </Form>
              </CardBody>
            </Card>
          </Col>
        </Row>
        <Row className="h-50">
          <Col md="6">
            <Card>
              <CardHeader>
                <h5 className="title">Weekly Teaching Activity</h5>
              </CardHeader>
              <CardBody>
                <LineGraph data={weeklyDataset} />
              </CardBody>
            </Card>
          </Col>
          <Col md="6" className="h-30">
            <Card>
              <CardHeader>
                <h5 className="title">Monthly Teaching Activity</h5>
              </CardHeader>
              <CardBody>
                <LineGraph data={monthlyDataset} />
              </CardBody>
            </Card>
          </Col>
        </Row>
        <Row>
          <Col md="12" className="order-1 md:order-2">
            <Card>
              <CardHeader>
                <h5 className="title">My Details</h5>
              </CardHeader>
              <CardBody>
                <Form>
                  <Row>
                    <Col className="pr-md-1" md="3">
                      <FormGroup>
                        <label>Personnel ID</label>
                        <Input
                          disabled
                          value={staff?.personnelId || ""}
                          className="text-light"
                          type="text"
                        />
                      </FormGroup>
                    </Col>
                    <Col className="px-md-1" md="3">
                      <FormGroup>
                        <label>Username</label>
                        <Input
                          disabled
                          type="text"
                          className="text-light"
                          value={staff?.username || ""}
                        />
                      </FormGroup>
                    </Col>
                    <Col className="pl-md-1" md="3">
                      <FormGroup>
                        <label>Email address</label>
                        <Input
                          disabled
                          type="email"
                          className="text-light"
                          value={staff?.email || ""}
                        />
                      </FormGroup>
                    </Col>
                    <Col className="pl-md-1" md="3">
                      <FormGroup>
                        <label>Birthday</label>
                        <Input
                          value={staff?.birthday || ""}
                          className="text-light"
                          type="date"
                          disabled
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col className="pr-md-1" md="3">
                      <FormGroup>
                        <label>Official Start Date</label>
                        <Input
                          value={staff?.officialStartDate}
                          disabled
                          className="text-light"
                          type="date"
                        />
                      </FormGroup>
                    </Col>
                    <Col className="pl-md-1" md="3">
                      <FormGroup>
                        <label>Status</label>
                        <Input
                          value={staff?.status || ""}
                          className="text-light"
                          type="text"
                          disabled
                        />
                      </FormGroup>
                    </Col>
                    <Col className="pr-md-1" md="3">
                      <FormGroup>
                        <label>Region</label>
                        <Input
                          value={staff?.region || ""}
                          disabled
                          className="text-light"
                          type="text"
                        />
                      </FormGroup>
                    </Col>
                    <Col className="pl-md-1" md="3">
                      <FormGroup>
                        <label>Old Teaching Hours</label>
                        <Input
                          disabled
                          value={staff?.legacyTotalTeachingHours || "0"}
                          className="text-light"
                          type="text"
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                </Form>
              </CardBody>
            </Card>
          </Col>
        </Row>
        <Row>
          <Col md="12" className="order-1 md:order-2">
            <Card>
              <CardHeader>
                <h5 className="title">Leaves Data</h5>
              </CardHeader>
              <CardBody>
                <Form>
                  <Row>
                    <Col className="pr-md-1" md="3">
                      <FormGroup>
                        <label>Total AL (Days)</label>
                        <Input
                          value={staff?.totalAnnualLeaves}
                          disabled
                          className="text-white"
                          type="text"
                        />
                      </FormGroup>
                    </Col>
                    <Col className="pl-md-1" md="3">
                      <FormGroup>
                        <label>AL Taken (Days)</label>
                        <Input
                          value={staff?.annualLeaveDaysUsed}
                          disabled
                          className="text-white"
                          type="text"
                        />
                      </FormGroup>
                    </Col>
                    <Col className="pr-md-1" md="3">
                      <FormGroup>
                        <label>AL Available (Days)</label>
                        <Input
                          value={staff?.annualLeaveDaysCount}
                          disabled
                          className="text-white"
                          type="text"
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col className="pr-md-1" md="3">
                      <FormGroup>
                        <label>Total OT Done (Hours)</label>
                        <Input
                          value={staff?.totalCompensationLeaves}
                          disabled
                          className="text-white"
                          type="text"
                        />
                      </FormGroup>
                    </Col>
                    <Col className="pl-md-1" md="3">
                      <FormGroup>
                        <label>CL Taken (Hours)</label>
                        <Input
                          value={staff?.compensationLeaveHoursUsed}
                          disabled
                          className="text-white"
                          type="text"
                        />
                      </FormGroup>
                    </Col>
                    <Col className="pr-md-1" md="3">
                      <FormGroup>
                        <label>CL Available (Hours)</label>
                        <Input
                          value={staff?.compensationLeaveHoursCount}
                          disabled
                          className="text-white"
                          type="text"
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col className="pr-md-1" md="3">
                      <FormGroup>
                        <label>SL Taken (Hours)</label>
                        <Input
                          value={staff?.sickLeaveDaysCount}
                          disabled
                          className="text-white"
                          type="text"
                        />
                      </FormGroup>
                    </Col>
                    <Col className="pl-md-1" md="3">
                      <FormGroup>
                        <label>NPL Taken (Hours)</label>
                        <Input
                          value={staff?.unpaidLeaveDaysCount}
                          disabled
                          className="text-white"
                          type="text"
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                </Form>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </>
  );
};
